/* eslint-disable max-len */
import Cached from '@mui/icons-material/Cached';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { companyFilter } from '../../apollo/reactive-variables/filters';
import dayjs from '../../config/dayjs';
import { ORDERING_DOCUMENTLIST_TYPE } from '../../constants';
import { FORM_SELECT_AUTOCOMPLETE_CLASS_NAME } from '../../theme/constants/autocomplete';
import { formatMinWordDate } from '../../helpers/date';
import filtersApplied from '../../helpers/filters';
import { useBooleanState, useLastRefreshDate } from '../../hooks';
import { LoadingIconButton } from '../buttons';
import FilterButton from '../buttons/FilterButton';
import RoundedRowStack from '../layout/RoundedRowStack';
import LastUpdatedDate from '../text/LastUpdatedDate';
import CollectorExecutiveFilter from './CollectorExecutiveFilter';
import CompanyFilter from './CompanyFilter';
import DatesFilter from './DatesFilter';
import ExecutiveFilter from './ExecutiveFilter';
import FingoPreOfferFilter from './FingoPreOfferFilter';
import FingoQuickFilter from './FingoQuickFilter';
import HuntingFarmingFilter from './HuntingFarmingFilter';
import INVOICE_OPERATION_STATES_OPTIONS from '../../constants/invoice-operation-states-options';
import InvoiceStatusFilter from './InvoiceStatusFilter';
import LightningPaymentAuthorizedFilter from './LightningPaymentAuthorizedFilter';
import BooleanSwitchFilter from './BooleanSwitchFilter';
import MasterEntitySourceFilter from './MasterEntitySourceFilter';
import PendingRestrictionsFilter from './PendingRestrictionsFilter';
import StatusFilter from './StatusFilter';
import ValidSiiCredentialsFilter from './ValidSiiCredentialsFilter';
import selectedCompanyFilter from './selectedCompanyFilter';

const FilterBadge = styled(Badge)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flex: '1 1 0px',
  },
}));

const FilterTypography = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('s')]: {
    fontSize: 11,
  },
}));

const Filters = ({
  type,
  dates,
  setDates,
  financingStatusOptions,
  allCompaniesOptions,
  states,
  setStates,
  setCustomVariables,
  customVariables,
  flexEndButtons,
  showPreoffersFilters,
  showQuickFilters,
  showValidSiiCredentialsFilter,
  showLightningFilter,
  showBooleanSwitchFilter,
  booleanSwitchFilterConfig,
  showPendingRestrictionsFilter,
  showStatesFilter,
  showDatesFilter,
  showCompanyIssuerFilter,
  showHuntingFarmingFilter,
  showMasterEntitySourceFilter,
  showCollectorExecutiveFilter,
  showInvoiceStatusFilters,
  invoiceStatusFilterOptions,
  defaultFilterDays,
  showRefresh,
  onRefresh,
  loading,
  showExportInvoice,
  exportExcelIconButton,
  buttonsToShow,
  showExecutiveFilter,
}) => {
  const [datesAnchorEl, setDatesAnchorEl] = useState(null);
  const [openStateFilter, setOpenStateFilter] = useState(false);
  const [lastUpdated, refreshDateHandler] = useLastRefreshDate();
  const openDateFilter = ({ currentTarget }) => setDatesAnchorEl(currentTarget);
  const closeDateFilter = () => setDatesAnchorEl(null);
  const [, , setStatesClear, setStatesFull] = useBooleanState(false);
  const numberOfFiltersApplied = useMemo(() => filtersApplied(states));
  const refreshLastUpdated = useCallback(() => {
    onRefresh();
    refreshDateHandler();
  }, [onRefresh, refreshDateHandler]);
  const [selectedCompany, setSelectedCompany] = useState(
    companyFilter().selectedCompany,
  );
  useEffect(() => {
    if (showCompanyIssuerFilter) {
      selectedCompanyFilter(
        selectedCompany,
        setCustomVariables,
        defaultFilterDays,
      );
    }
  }, [selectedCompany]);
  return (
    <RoundedRowStack spacing={2}>
      {showRefresh && (
        <LoadingIconButton
          onClick={refreshLastUpdated}
          sx={{ mr: { xs: '0', md: 'auto' } }}
          loading={loading}
          color="gray"
          id="refresh-documents"
          disableRipple
          disableTouchRipple
          disableElevation
        >
          <Cached />
          <LastUpdatedDate lastUpdated={lastUpdated} />
        </LoadingIconButton>
      )}
      {showValidSiiCredentialsFilter && (
        <ValidSiiCredentialsFilter states={states} setStates={setStates} />
      )}
      {showLightningFilter && (
        <LightningPaymentAuthorizedFilter
          states={states}
          setStates={setStates}
        />
      )}
      {showBooleanSwitchFilter && (
        <BooleanSwitchFilter
          customVariables={customVariables}
          setCustomVariables={setCustomVariables}
          {...booleanSwitchFilterConfig}
        />
      )}
      {showPendingRestrictionsFilter && (
        <PendingRestrictionsFilter states={states} setStates={setStates} />
      )}
      {showPreoffersFilters && (
        <FingoPreOfferFilter
          states={states}
          setStates={setStates}
          filterType={
            type === ORDERING_DOCUMENTLIST_TYPE ? 'ORDERING' : 'FACTORING'
          }
        />
      )}
      {showInvoiceStatusFilters && (
        <InvoiceStatusFilter
          states={states}
          setStates={setStates}
          options={invoiceStatusFilterOptions}
        />
      )}
      {showQuickFilters && (
        <FingoQuickFilter
          states={states}
          setStates={setStates}
          buttonsToShow={buttonsToShow}
        />
      )}
      {showStatesFilter && (
        <>
          <FilterBadge badgeContent={numberOfFiltersApplied} color="primary">
            <FilterButton
              onClick={() => setOpenStateFilter(true)}
              id="open-filters"
            >
              <FilterTypography variant="subNavItem">Filtros</FilterTypography>
            </FilterButton>
          </FilterBadge>
          <StatusFilter
            openStateFilter={openStateFilter}
            close={() => setOpenStateFilter(false)}
            states={states}
            financingStatusOptions={financingStatusOptions}
            allCompaniesOptions={allCompaniesOptions}
            setStates={setStates}
            setStatesClear={setStatesClear}
            setStatesFull={setStatesFull}
          />
        </>
      )}
      {showDatesFilter && (
        <>
          <FilterButton
            isdate="true"
            onClick={openDateFilter}
            id="filter-dates"
          >
            <FilterTypography variant="subNavItem">
              {`${formatMinWordDate(
                dates.dateIssued_Gte,
              )} - ${formatMinWordDate(dates.dateIssued_Lte)}`}
            </FilterTypography>
          </FilterButton>
          <DatesFilter
            anchorEl={datesAnchorEl}
            close={closeDateFilter}
            dates={dates}
            setDates={setDates}
            states={states}
            setStates={setStates}
          />
        </>
      )}
      {showCompanyIssuerFilter && (
        <CompanyFilter
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          inputLabel={
            selectedCompany
              ? selectedCompany.displayNationalIdentifier
              : 'Todas - Últimos 14 días'
          }
          className={FORM_SELECT_AUTOCOMPLETE_CLASS_NAME}
          usePlaceholder
          sx={{ width: '250px' }}
        />
      )}
      {showExecutiveFilter && (
        <ExecutiveFilter
          setExecutiveCallback={(node) => setStates((rest) => ({
            ...rest,
            companyExecutiveAssignedId: node?.graphqlId || null,
          }))}
        />
      )}
      {showHuntingFarmingFilter && (
        <HuntingFarmingFilter
          setHuntingFarmingCallback={(value) => setStates((rest) => ({ ...rest, farmingByCommercialType: value }))}
        />
      )}
      {showMasterEntitySourceFilter && (
        <MasterEntitySourceFilter
          setMasterEntitySourceCallback={(value) => setStates((rest) => ({ ...rest, masterEntitySource: value }))}
        />
      )}
      {showCollectorExecutiveFilter && (
        <CollectorExecutiveFilter
          setCollectorExecutiveCallback={(value) => {
            if (value) {
              setStates((rest) => ({
                ...rest,
                collectionManagerRelation_Collector_Email: value,
              }));
            } else {
              setStates((rest) => {
                const newState = { ...rest };
                delete newState.collectionManagerRelation_Collector_Email;
                return newState;
              });
            }
          }}
        />
      )}
      {showExportInvoice && exportExcelIconButton}
      {flexEndButtons()}
    </RoundedRowStack>
  );
};

Filters.propTypes = {
  type: PropTypes.string,
  financingStatusOptions: PropTypes.bool,
  allCompaniesOptions: PropTypes.bool,
  dates: PropTypes.shape({
    dateIssued_Gte: PropTypes.instanceOf(dayjs),
    dateIssued_Lte: PropTypes.instanceOf(dayjs),
  }),
  setDates: PropTypes.func,
  setCustomVariables: PropTypes.func.isRequired,
  states: PropTypes.shape({
    siiStatus: PropTypes.arrayOf(PropTypes.string),
    loanedStatus: PropTypes.arrayOf(PropTypes.string),
    creditNoteStatus: PropTypes.arrayOf(PropTypes.string),
    documentFinanceStatus: PropTypes.arrayOf(PropTypes.string),
  }),
  setStates: PropTypes.func,
  customVariables: PropTypes.shape(),
  flexEndButtons: PropTypes.func,
  showQuickFilters: PropTypes.bool,
  showValidSiiCredentialsFilter: PropTypes.bool,
  showLightningFilter: PropTypes.bool,
  showBooleanSwitchFilter: PropTypes.bool,
  booleanSwitchFilterConfig: PropTypes.shape({
    label: PropTypes.string,
    checkedKey: PropTypes.string,
    checkedValue: PropTypes.bool,
    onCustomVariables: PropTypes.shape(),
    offCustomVariables: PropTypes.shape(),
  }),
  showPendingRestrictionsFilter: PropTypes.bool,
  showStatesFilter: PropTypes.bool,
  showDatesFilter: PropTypes.bool,
  showPreoffersFilters: PropTypes.bool,
  showInvoiceStatusFilters: PropTypes.bool,
  invoiceStatusFilterOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    filter: PropTypes.shape({
      status: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
  })),
  defaultFilterDays: PropTypes.number,
  showCompanyIssuerFilter: PropTypes.bool,
  showHuntingFarmingFilter: PropTypes.bool,
  showMasterEntitySourceFilter: PropTypes.bool,
  showCollectorExecutiveFilter: PropTypes.bool,
  showRefresh: PropTypes.bool,
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  showExportInvoice: PropTypes.bool,
  exportExcelIconButton: PropTypes.node,
  buttonsToShow: PropTypes.arrayOf(PropTypes.string),
  showExecutiveFilter: PropTypes.bool,
};

Filters.defaultProps = {
  type: '',
  defaultFilterDays: 3,
  dates: null,
  financingStatusOptions: false,
  allCompaniesOptions: false,
  setDates: () => {},
  states: {
    siiStatus: [],
    loanedStatus: [],
    creditNoteStatus: [],
    allCompanies: [],
    documentFinanceStatus: [],
  },
  setStates: () => {},
  customVariables: {},
  flexEndButtons: () => <></>,
  showStatesFilter: true,
  showCompanyIssuerFilter: false,
  showHuntingFarmingFilter: false,
  showDatesFilter: true,
  showQuickFilters: false,
  showValidSiiCredentialsFilter: false,
  showLightningFilter: false,
  showBooleanSwitchFilter: false,
  booleanSwitchFilterConfig: {},
  showPendingRestrictionsFilter: false,
  showPreoffersFilters: false,
  showInvoiceStatusFilters: false,
  invoiceStatusFilterOptions: INVOICE_OPERATION_STATES_OPTIONS,
  showRefresh: false,
  showMasterEntitySourceFilter: false,
  showCollectorExecutiveFilter: false,
  onRefresh: () => {},
  loading: false,
  showExportInvoice: true,
  exportExcelIconButton: null,
  buttonsToShow: [],
  showExecutiveFilter: false,
};

export default Filters;
